import React, { useState } from 'react';
import { signInWithGoogle, signInWithEmail, signUpWithEmail, sendPasswordReset } from '../auth';
import { Mail, ArrowLeft } from 'lucide-react';

export default function AuthModal({ onSignIn, onError, loading }) {
  const [authMode, setAuthMode] = useState('google');
  const [emailMode, setEmailMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const handleGoogleSignIn = async () => {
    try {
      const user = await signInWithGoogle();
      await onSignIn(user);
    } catch (e) {
      onError(e.message || 'Google sign-in failed');
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (emailMode === 'reset') {
      try {
        await sendPasswordReset(email);
        setResetSent(true);
      } catch (e) {
        onError(e.message || 'Failed to send reset email');
      }
      return;
    }

    try {
      const user = emailMode === 'signup'
        ? await signUpWithEmail(email, password, displayName || undefined)
        : await signInWithEmail(email, password);
      await onSignIn(user);
    } catch (e) {
      const msg = e.code === 'auth/email-already-in-use'
        ? 'Email already in use. Try signing in instead.'
        : e.code === 'auth/weak-password'
        ? 'Password should be at least 6 characters.'
        : e.code === 'auth/invalid-email'
        ? 'Invalid email address.'
        : e.code === 'auth/user-not-found' || e.code === 'auth/wrong-password'
        ? 'Invalid email or password.'
        : e.message || 'Authentication failed';
      onError(msg);
    }
  };

  if (resetSent) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-xl max-w-md w-full">
        <p className="text-lg font-semibold text-green-600 mb-2">Password reset email sent! ✓</p>
        <p className="text-gray-600 mb-4">Check your inbox for reset instructions.</p>
        <button
          onClick={() => { setResetSent(false); setEmailMode('signin'); }}
          className="text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-8 shadow-xl max-w-md w-full">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">
        {emailMode === 'signup' ? 'Create Account' : emailMode === 'reset' ? 'Reset Password' : 'Welcome Back'}
      </h2>
      <p className="text-gray-600 mb-6">
        {emailMode === 'signup'
          ? 'Get alerts for perfect beach days'
          : emailMode === 'reset'
          ? 'Enter your email to receive a password reset link. Note: Google sign-in accounts don\'t have passwords.'
          : 'Sign in to manage your beach day preferences'}
      </p>

      {authMode === 'google' ? (
        <div className="flex flex-col gap-4">
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full px-6 py-3 bg-white border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-all disabled:opacity-50 flex items-center justify-center gap-3 shadow-sm"
          >
            {loading ? 'Signing in…' : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </>
            )}
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or</span>
            </div>
          </div>

          <button
            onClick={() => setAuthMode('email')}
            className="w-full px-6 py-3 bg-gray-100 rounded-lg font-medium text-gray-700 hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
          >
            <Mail className="w-5 h-5" />
            Continue with Email
          </button>
        </div>
      ) : (
        <form onSubmit={handleEmailAuth} className="flex flex-col gap-4">
          <input
            type="email"
            required
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
          />
          {emailMode !== 'reset' && (
            <input
              type="password"
              required
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
            />
          )}
          {emailMode === 'signup' && (
            <input
              type="text"
              placeholder="Your name (optional)"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
            />
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all disabled:opacity-50 shadow-lg"
          >
            {loading ? 'Please wait…' :
              emailMode === 'signup' ? 'Create Account' :
              emailMode === 'reset' ? 'Send Reset Link' :
              'Sign In'}
          </button>
          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => setAuthMode('google')}
              className="text-gray-600 hover:text-gray-800 transition-colors flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div className="flex gap-2">
              {emailMode === 'signin' && (
                <>
                  <button
                    type="button"
                    onClick={() => setEmailMode('reset')}
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    Forgot password?
                  </button>
                  <span className="text-gray-400">•</span>
                  <button
                    type="button"
                    onClick={() => setEmailMode('signup')}
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    Sign up
                  </button>
                </>
              )}
              {emailMode === 'signup' && (
                <button
                  type="button"
                  onClick={() => setEmailMode('signin')}
                  className="text-blue-600 hover:text-blue-700 transition-colors"
                >
                  Already have an account?
                </button>
              )}
              {emailMode === 'reset' && (
                <button
                  type="button"
                  onClick={() => setEmailMode('signin')}
                  className="text-blue-600 hover:text-blue-700 transition-colors"
                >
                  Back to sign in
                </button>
              )}
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
